<mat-card class="rounded-3xl border border-slate-200 p-4 shadow-lg shadow-slate-200/60">
  <h3 class="mb-3 text-lg font-semibold text-slate-900">Consent</h3>

  <div class="mb-4 grid gap-2 md:grid-cols-2">
    @for (item of visibleTypes(); track item.type) {
      <div class="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 p-3 text-sm">
        <div class="flex items-center gap-2">
          <mat-icon
            class="!text-base"
            [class.text-green-600]="interventionStore.hasConsent(item.type)"
            [class.text-slate-400]="!interventionStore.hasConsent(item.type)">
            {{ interventionStore.hasConsent(item.type) ? 'check_circle' : 'radio_button_unchecked' }}
          </mat-icon>
          <span class="font-medium text-slate-900">{{ item.label }}</span>
          <span class="text-slate-500">{{ interventionStore.hasConsent(item.type) ? 'in force' : interventionStore.isConsentDeclined(item.type) ? 'declined' : 'not recorded' }}</span>
        </div>
        @if (canRecord()[item.type] && recordingType() !== item.type) {
          <button mat-stroked-button type="button" (click)="open(item.type)">
            {{ interventionStore.hasConsent(item.type) ? 'Record again' : 'Record' }}
          </button>
        }
      </div>
    }
  </div>

  @if (recordingType(); as type) {
    <form [formGroup]="form" (ngSubmit)="save()" class="mb-4 grid gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <h4 class="font-semibold text-slate-900">Record {{ label(type).toLowerCase() }}</h4>
      <!-- The wording that is read to the member before the answer is taken -->
      @if (script(); as consentScript) {
        @if (consentScript.script) {
          <blockquote class="rounded-xl border-l-4 border-slate-300 bg-white p-3 text-sm italic text-slate-700">{{ consentScript.script }}</blockquote>
        }
      }
      <!-- What the member decided; a refusal flags the case and does not close it -->
      <mat-radio-group formControlName="decision" class="flex gap-6" aria-label="Decision">
        <mat-radio-button value="Granted">Granted</mat-radio-button>
        <mat-radio-button value="Declined">Declined</mat-radio-button>
      </mat-radio-group>
      <div class="grid gap-4 md:grid-cols-2">
        <mat-form-field appearance="outline">
          <mat-label>Given by</mat-label>
          <input matInput maxlength="200" formControlName="givenBy" placeholder="The member, or who consented for them" />
        </mat-form-field>
        @if (form.controls.decision.value === 'Granted') {
          <mat-form-field appearance="outline">
            <mat-label>Expiry date</mat-label>
            <input matInput [matDatepicker]="expiryPicker" formControlName="expiryDate" />
            <mat-datepicker-toggle matIconSuffix [for]="expiryPicker"></mat-datepicker-toggle>
            <mat-datepicker #expiryPicker></mat-datepicker>
          </mat-form-field>
        }
        <mat-form-field appearance="outline" class="md:col-span-2">
          <mat-label>Scope</mat-label>
          <textarea matInput rows="2" maxlength="500" formControlName="scope" placeholder="What the member agreed to"></textarea>
        </mat-form-field>
      </div>
      @if (form.controls.decision.value === 'Declined' && script()?.refusedMessage) {
        <p class="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{{ script()?.refusedMessage }}</p>
      }
      <p class="text-sm text-slate-500">Recording is dated now. Recording again adds a new version; earlier versions stay on the record.</p>
      <div class="flex justify-end gap-2">
        <button mat-stroked-button type="button" (click)="closeForm()">Close</button>
        <button mat-flat-button type="submit" [disabled]="interventionStore.savingConsent()">
          @if (interventionStore.savingConsent()) {
            <mat-spinner diameter="20" class="mr-2" />
          }
          Record consent
        </button>
      </div>
    </form>
  }

  @if (interventionStore.consentsLoading() && interventionStore.consents().length === 0) {
    <div class="flex items-center gap-3 text-sm text-slate-600">
      <mat-spinner diameter="20" />
      Loading consent...
    </div>
  } @else if (interventionStore.consents().length === 0) {
    <p class="text-sm text-slate-500">No consent has been recorded yet.</p>
  } @else {
    <div class="grid gap-2">
      @for (consent of interventionStore.consents(); track consent.id) {
        <div class="grid gap-2 border-b border-slate-100 py-2 text-sm last:border-b-0">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="grid gap-0.5" [class.opacity-60]="consent.status !== 'Active'">
              <div class="flex items-center gap-2">
                <span class="font-medium text-slate-900">{{ label(consent.consentType) }} · version {{ consent.version }}</span>
                <span
                  class="rounded-full px-2 py-0.5 text-xs font-medium"
                  [class.bg-green-50]="consent.status === 'Active'"
                  [class.text-green-700]="consent.status === 'Active'"
                  [class.bg-amber-50]="consent.status === 'Withdrawn' || consent.status === 'Expired'"
                  [class.text-amber-700]="consent.status === 'Withdrawn' || consent.status === 'Expired'"
                  [class.bg-red-50]="consent.status === 'Declined'"
                  [class.text-red-700]="consent.status === 'Declined'"
                  [class.bg-slate-100]="consent.status === 'Superseded'"
                  [class.text-slate-500]="consent.status === 'Superseded'">
                  {{ consent.status }}
                </span>
              </div>
              <span class="text-slate-600">
                Recorded {{ consent.decidedDate | date: 'yyyy-MM-dd HH:mm' }}
                @if (consent.givenBy) {
                  · by {{ consent.givenBy }}
                }
                @if (consent.expiryDate) {
                  · expires {{ consent.expiryDate }}
                }
              </span>
              @if (consent.scope) {
                <span class="text-slate-500">{{ consent.scope }}</span>
              }
              @if (consent.withdrawnReason) {
                <span class="text-amber-700">
                  Withdrawn {{ consent.withdrawnDate | date: 'yyyy-MM-dd' }}: {{ consent.withdrawnReason }}
                </span>
              }
            </div>

            @if (consent.status === 'Active' && canRecord()[consent.consentType] && withdrawingId() !== consent.id) {
              <button mat-stroked-button type="button" (click)="openWithdraw(consent)">Withdraw</button>
            }
          </div>

          @if (withdrawingId() === consent.id) {
            <div class="flex flex-wrap items-start gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <mat-form-field appearance="outline" class="min-w-64 flex-1">
                <mat-label>Reason for withdrawing *</mat-label>
                <input matInput maxlength="500" [formControl]="withdrawReason" />
              </mat-form-field>
              <button mat-stroked-button type="button" (click)="closeWithdraw()">Close</button>
              <button
                mat-flat-button
                type="button"
                [disabled]="!withdrawReason.value.trim() || interventionStore.savingConsent()"
                (click)="withdraw(consent)">
                Withdraw consent
              </button>
            </div>
          }
        </div>
      }
    </div>
  }
</mat-card>
